import React, { useCallback, useEffect, useRef, useState } from 'react';
import AttendRoundModal from '../interview/AttendRoundModal';
import { MOCK_INTERVIEW_PUBLISHED_NOTICE } from '../../features/interview/constants';
import { useFloatingAssistVisibility } from '../../hooks/useFloatingAssistVisibility';
import { useFloatingAssistPosition } from '../../hooks/useFloatingAssistPosition';
import {
  useFloatingAssistHints,
  ackPreparationAudioPlayback,
  type FloatingAssistAction,
} from '../../hooks/useFloatingAssistHints';
import { useQuickLaunch } from '../../hooks/useQuickLaunch';
import { useAppSelector } from '../../store';
import { fetchAuthenticatedAudio } from '../../utils/constants';
import './FloatingAssistWidget.css';

const FADE_DELAY_MS = 2000;
const AUTOPLAY_CHIME_URL = '/notification.mp3';
const AUTOPLAY_CHIME_DELAY_MS = 2000;

const PlayIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 4.98951C5 4.01835 5 3.53277 5.20249 3.2651C5.37889 3.03191 5.64852 2.88761 5.9404 2.87018C6.27544 2.85017 6.67946 3.11953 7.48752 3.65823L18.0031 10.6686C18.6708 11.1137 19.0046 11.3363 19.1209 11.6168C19.2227 11.8621 19.2227 12.1377 19.1209 12.383C19.0046 12.6635 18.6708 12.886 18.0031 13.3312L7.48752 20.3415C6.67946 20.8802 6.27544 21.1496 5.9404 21.1296C5.64852 21.1122 5.37889 20.9679 5.20249 20.7347C5 20.467 5 19.9814 5 19.0103V4.98951Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CornerArrowIcon: React.FC<{ rotation: number }> = ({ rotation }) => (
  <svg
    className="floating-assist__corner-arrow-icon"
    viewBox="0 0 16 16"
    aria-hidden="true"
    style={{ transform: `rotate(${rotation}deg)` }}
  >
    <path d="M8 2.5 L12.8 12.5 H3.2 Z" fill="currentColor" />
  </svg>
);

const QuickLaunchIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M9 17.5H3.5M6.5 12H2M9 6.5H4M17 3L10.4036 12.235C10.1116 12.6438 9.96562 12.8481 9.97194 13.0185C9.97744 13.1669 10.0486 13.3051 10.1661 13.3958C10.3011 13.5 10.5522 13.5 11.0546 13.5H16L15 21L21.5964 11.765C21.8884 11.3562 22.0344 11.1519 22.0281 10.9815C22.0226 10.8331 21.9514 10.6949 21.8339 10.6042C21.6989 10.5 21.4478 10.5 20.9454 10.5H16L17 3Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const useDelayedActiveState = (keepActive: boolean) => {
  const [isActive, setIsActive] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFadeTimer = useCallback(() => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  const activate = useCallback(() => {
    clearFadeTimer();
    setIsActive(true);
  }, [clearFadeTimer]);

  const scheduleDeactivate = useCallback(() => {
    clearFadeTimer();
    fadeTimerRef.current = setTimeout(() => {
      setIsActive(false);
      fadeTimerRef.current = null;
    }, FADE_DELAY_MS);
  }, [clearFadeTimer]);

  useEffect(() => {
    if (keepActive) {
      activate();
    }
  }, [keepActive, activate]);

  useEffect(() => () => clearFadeTimer(), [clearFadeTimer]);

  return { isActive: isActive || keepActive, activate, scheduleDeactivate };
};

const FloatingAssistWidget: React.FC = () => {
  const visible = useFloatingAssistVisibility();
  const hints = useFloatingAssistHints();
  const quickLaunch = useQuickLaunch(visible);
  const { user } = useAppSelector((state) => state.auth);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const autoPlayedKeyRef = useRef<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [hoveredAction, setHoveredAction] = useState<FloatingAssistAction | null>(null);

  const { isActive, activate, scheduleDeactivate } = useDelayedActiveState(isPlaying);
  const { rootRef, anchorClass, moveToCorner, cornerOptions } = useFloatingAssistPosition();

  const showCornerArrows = isActive;

  useEffect(() => {
    if (visible) {
      void quickLaunch.refresh();
    }
  }, [visible, quickLaunch.refresh]);

  useEffect(() => {
    let cancelled = false;
    let detachListeners: (() => void) | null = null;

    const loadAudio = async () => {
      setAudioReady(false);
      setIsPlaying(false);

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }

      try {
        const sourceUrl = hints.playAudioUrl;
        const resolvedUrl = sourceUrl.startsWith('/api/audio')
          ? await fetchAuthenticatedAudio(sourceUrl)
          : sourceUrl;

        if (cancelled) {
          if (resolvedUrl.startsWith('blob:')) {
            URL.revokeObjectURL(resolvedUrl);
          }
          return;
        }

        if (resolvedUrl.startsWith('blob:')) {
          blobUrlRef.current = resolvedUrl;
        }

        const audio = new Audio(resolvedUrl);
        audioRef.current = audio;

        const handleEnded = () => setIsPlaying(false);
        const handlePause = () => setIsPlaying(false);
        const handlePlay = () => setIsPlaying(true);

        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('play', handlePlay);
        detachListeners = () => {
          audio.pause();
          audio.removeEventListener('ended', handleEnded);
          audio.removeEventListener('pause', handlePause);
          audio.removeEventListener('play', handlePlay);
        };
        setAudioReady(true);
      } catch {
        if (!cancelled) {
          setAudioReady(false);
        }
      }
    };

    void loadAudio();

    return () => {
      cancelled = true;
      detachListeners?.();
      audioRef.current = null;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [hints.playAudioUrl, hints.insightsCacheKey]);

  useEffect(() => {
    if (
      !visible ||
      !audioReady ||
      !hints.autoPlayPreparationAudio ||
      !hints.isPersonalizedAudio
    ) {
      return;
    }

    const playKey = hints.insightsCacheKey ?? hints.playAudioUrl;
    if (autoPlayedKeyRef.current === playKey) {
      return;
    }

    const prepAudio = audioRef.current;
    if (!prepAudio) {
      return;
    }

    autoPlayedKeyRef.current = playKey;

    let cancelled = false;
    const chime = new Audio(AUTOPLAY_CHIME_URL);

    const playPreparation = () => {
      if (cancelled) {
        return;
      }
      activate();
      prepAudio.currentTime = 0;
      void prepAudio
        .play()
        .then(() => {
          if (cancelled) {
            return;
          }
          if (user?.id) {
            void ackPreparationAudioPlayback(user.id).catch(() => {
              /* non-blocking; user can still replay manually */
            });
          }
        })
        .catch(() => setIsPlaying(false));
    };

    const onChimeEnded = () => {
      chime.removeEventListener('ended', onChimeEnded);
      playPreparation();
    };

    chime.addEventListener('ended', onChimeEnded);

    const delayTimer = setTimeout(() => {
      if (cancelled) {
        return;
      }
      void chime.play().catch(() => {
        chime.removeEventListener('ended', onChimeEnded);
        playPreparation();
      });
    }, AUTOPLAY_CHIME_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(delayTimer);
      chime.pause();
      chime.removeEventListener('ended', onChimeEnded);
    };
  }, [
    visible,
    audioReady,
    hints.autoPlayPreparationAudio,
    hints.isPersonalizedAudio,
    hints.insightsCacheKey,
    hints.playAudioUrl,
    user?.id,
    activate,
  ]);

  const toggleAudio = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      activate();

      const audio = audioRef.current;
      if (!audio || !audioReady) return;

      if (isPlaying) {
        audio.pause();
        return;
      }

      audio.currentTime = 0;
      void audio.play().catch(() => setIsPlaying(false));
    },
    [activate, audioReady, isPlaying],
  );

  const handleActionEnter = useCallback(
    (action: FloatingAssistAction) => {
      activate();
      setHoveredAction(action);
    },
    [activate],
  );

  if (!visible) {
    return null;
  }

  const widgetClassName = [
    'floating-assist',
    'floating-assist--hint-left',
    isActive && 'floating-assist--active',
    isPlaying && 'floating-assist--playing',
    hoveredAction && 'floating-assist--drawer-open',
  ]
    .filter(Boolean)
    .join(' ');

  const rootClassName = ['floating-assist-root', anchorClass].filter(Boolean).join(' ');

  const panelAnchorClassName = [
    'floating-assist__panel-anchor',
    showCornerArrows && 'floating-assist__panel-anchor--repositioning',
  ]
    .filter(Boolean)
    .join(' ');

  const drawerText = hoveredAction
    ? hoveredAction === 'quick'
      ? quickLaunch.hint
      : hints[hoveredAction]
    : '';

  return (
    <div
      ref={rootRef}
      className={rootClassName}
      onMouseEnter={activate}
      onMouseLeave={() => {
        setHoveredAction(null);
        scheduleDeactivate();
      }}
    >
      <div className={widgetClassName} role="complementary" aria-label="Quick actions">
        <div
          className={`floating-assist__drawer${hoveredAction ? ' floating-assist__drawer--open' : ''}`}
          aria-hidden={!hoveredAction}
        >
          <p className="floating-assist__drawer-text">{drawerText}</p>
        </div>

        <div className={panelAnchorClassName}>
          {showCornerArrows &&
            cornerOptions.map((option) => (
              <button
                key={option.target}
                type="button"
                className={`floating-assist__corner-arrow floating-assist__corner-arrow--${option.placement}`}
                aria-label={option.label}
                onClick={(event) => {
                  event.stopPropagation();
                  activate();
                  moveToCorner(option.target);
                }}
              >
                <CornerArrowIcon rotation={option.rotation} />
              </button>
            ))}

          <div className="floating-assist__panel">
          <button
            type="button"
            className={`floating-assist__action-btn floating-assist__action-btn--play${isPlaying ? ' floating-assist__action-btn--playing' : ''}`}
            onClick={toggleAudio}
            onMouseEnter={() => handleActionEnter('play')}
            onFocus={() => handleActionEnter('play')}
            aria-label={isPlaying ? 'Pause audio tip' : 'Play audio tip'}
            aria-describedby={hoveredAction === 'play' ? 'floating-assist-drawer-text' : undefined}
            disabled={!audioReady}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <rect x="7" y="6" width="3.5" height="12" rx="0.8" />
                <rect x="13.5" y="6" width="3.5" height="12" rx="0.8" />
              </svg>
            ) : (
              <PlayIcon />
            )}
          </button>

          <button
            type="button"
            className="floating-assist__action-btn floating-assist__action-btn--quick"
            aria-label="Quick launch"
            onMouseEnter={() => handleActionEnter('quick')}
            onFocus={() => handleActionEnter('quick')}
            aria-describedby={hoveredAction === 'quick' ? 'floating-assist-drawer-text' : undefined}
            disabled={quickLaunch.isLaunching || quickLaunch.isLoading}
            aria-busy={quickLaunch.isLaunching}
            onClick={(event) => {
              event.stopPropagation();
              activate();
              void quickLaunch.launch();
            }}
          >
            <QuickLaunchIcon />
          </button>
          </div>
        </div>

        <p id="floating-assist-drawer-text" className="floating-assist__sr-only">
          {drawerText}
        </p>
      </div>

      {quickLaunch.pendingConfirm &&
        quickLaunch.pendingConfirm.roundNum != null &&
        quickLaunch.pendingConfirm.roundType && (
          <AttendRoundModal
            roundNum={quickLaunch.pendingConfirm.roundNum}
            roundType={quickLaunch.pendingConfirm.roundType}
            loading={quickLaunch.isLaunching}
            error={quickLaunch.error ?? ''}
            loadingNotice={MOCK_INTERVIEW_PUBLISHED_NOTICE}
            showLanguagePicker={quickLaunch.showLanguagePicker}
            selectedLanguage={quickLaunch.selectedLanguage}
            onLanguageChange={quickLaunch.setSelectedLanguage}
            onClose={quickLaunch.cancelConfirm}
            onStart={quickLaunch.confirmLaunch}
          />
        )}
    </div>
  );
};

export default FloatingAssistWidget;
